/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { IMessageDescriptor } from '@scuf/localization/dist/types';
import _ from 'lodash';
import descriptors from '../../../../../locales/en-US/descriptors';
import { IReadOnboardingExcelData } from '../FileReaderUtils/FileReader';
import { IValidationResponse } from '../FileReaderUtils/FileValidator';
import { IAssociationData, IElementData, IPropertyData } from './DataConverter';
import { IAny } from '../../../../DashboardKPIWidgets/IEnergyManagementProps';

export const lowerCaseNoSpaces = (s: string): string => {
  let res = _.toLower(s);
  res = res.replace(/\s+/g, '');
  return res;
};

export const compareAfterLowerCaseNoSpaces = (
  s1: string,
  s2: string
): boolean => {
  return lowerCaseNoSpaces(s1) === lowerCaseNoSpaces(s2);
};

const eqSpaceMappingMsgs = descriptors.equipmentSpaceMapping;
const z = descriptors.ZLOOnboardingConfig;
const maxNameLength = 20;

export interface IValidationDetails {
  d: IMessageDescriptor;
  args?: object;
  type?: 'error' | 'info' | 'child';
}

export interface IDBPropertyObj {
  name: string;
  id: string;
  type: string;
  propertyRoleName: string;
  unit?: string;
}

export interface IDBSpatialElement {
  id: string;
  name: string;
  // properties?: IDBPropertyObj;
  [other: string]: IAny;
}

export interface IDBElement {
  id: string;
  name: string;
  types: string[];
  properties: IDBPropertyObj[];
  [other: string]: IAny;
}

export interface IPropertyValidation extends IPropertyData {
  isValid: boolean;
  validationDetails: IValidationDetails[];
}

export interface IElementDataValidations
  extends Omit<IElementData, 'associations' | 'properties'> {
  isValid: boolean;
  validationDetails: IValidationDetails[];
  validatedProperties: IPropertyValidation[];
}

export interface ISpatialEleValidation
  extends Omit<IAssociationData, 'properties'> {
  isValid: boolean; // validation only for name and id; association val in next interface
  validationDetails: IValidationDetails[]; // validation only for name and id; association val in next interface
  // validatedProperties: IPropertyValidation[];
}

export interface IAssociationDataValidations
  extends Omit<ISpatialEleValidation, 'associations' | 'properties'> {
  allValid: boolean;
  validatedAssociations: IElementDataValidations[];
}

export interface IAssocValidationResponse {
  allValid: boolean;
  validatedData: IAssociationDataValidations[];
  numErrors: number;
}

// validate data schema i.e headers
export const validateSchema = (
  readData: IReadOnboardingExcelData,
  requiredSubHeaders: IAny[]
): IValidationResponse => {
  const { headers } = readData;
  const _intersection = _.intersection(headers.subHeaders, requiredSubHeaders);
  if (_.isEqual(_.sortBy(_intersection), _.sortBy(requiredSubHeaders))) {
    return { success: true, messages: [] };
  }
  return {
    success: false,
    messages: [
      {
        d: eqSpaceMappingMsgs.INVALID_SCHEMA_REQUIRED_HEADERS
      }
    ]
  };
};

export const validateInitialContents = (
  readData: IReadOnboardingExcelData
): IValidationResponse => {
  try {
    const { jsonData } = readData;
    // check for no data
    if (_.isEmpty(jsonData)) {
      return { success: false, messages: [{ d: eqSpaceMappingMsgs.NO_DATA }] };
    }
    // check 1st row has SpatialElementName
    const { SpatialElementName } = jsonData[0];
    if (_.isEmpty(SpatialElementName)) {
      return {
        success: false,
        messages: [{ d: eqSpaceMappingMsgs.INVALID_DATA_CONTENT }]
      };
    }
    return { success: true, messages: [] };
  } catch (e) {
    return {
      success: false,
      messages: [
        {
          d: eqSpaceMappingMsgs.SOME_ERROR_GENERAL,
          args: { errorString: _.toString(e) }
        }
      ]
    };
  }
};

export const commonNameRegex = /^[a-zA-Z0-9][a-zA-Z0-9\s\_\-\(\)]*[a-zA-Z0-9]?$/;

export interface IProperyValidationFunctions {
  [propertyKey: string]: {
    validationFunction: (
      value: IAny
    ) => { truthy: boolean; message: IMessageDescriptor; args?: object };
  };
}

export interface IPropertyDataTypeValidationMap {
  [propertyDataType: string]: {
    validationFunction: (pValue: IAny) => { truthy: boolean };
    formattedValue: (pValue: IAny) => { convertedVal: IAny };
  };
}

const propertyValidationMap: IPropertyDataTypeValidationMap = {
  Numeric: {
    validationFunction: (
      pValue: IAny
    ): { truthy: boolean; convertedVal?: number } => {
      const _truthy = _.isNumber(pValue);
      if (_truthy) {
        return { truthy: _truthy };
      }
      return { truthy: _truthy };
    },
    formattedValue: (pValue: IAny) => {
      return { convertedVal: pValue };
    }
  },
  Boolean: {
    validationFunction: (pValue: IAny) => {
      const _truthy =
        _.isBoolean(pValue) ||
        pValue === '1' ||
        pValue === '0' ||
        pValue === 1 ||
        pValue === 0;
      if (_truthy) {
        return {
          truthy: _truthy
        };
      }
      return { truthy: _truthy };
    },
    formattedValue: (pValue: IAny) => {
      return { convertedVal: _.toString(_.toInteger(pValue)) };
    }
  }
};

export const useValidatorForAssociationAndConfig = () => {
  // Validate Complete Data
  const validateAssocData = (
    associationData: IAssociationData[],
    allSpatialElements: IDBSpatialElement[],
    allElements: IDBElement[]
  ): IAssocValidationResponse => {
    let numErrors = 0;
    const {
      numErrors: _numSpErrors,
      spatialElementsValidations: validateSpatialElements
    } = validateAllSpatialElements(associationData, allSpatialElements);
    numErrors += _numSpErrors;
    let allValid = true;
    const validatedData = _.map(validateSpatialElements, (validatedSpEle) => {
      const {
        numErrors: _numEleErrors,
        assocValidation: _v
      } = validateAssociation(validatedSpEle, allElements);
      allValid = allValid && _v.allValid;
      numErrors += _numEleErrors;
      return _v;
    });
    return { validatedData, allValid, numErrors };
  };

  // Helper Functions
  const validateProperty = (
    propertyValue: IAny,
    dbProperty: IDBPropertyObj
  ): { validationDetails: IValidationDetails[]; convertedValue: IAny } => {
    const validationDetails: IValidationDetails[] = [];
    let convertedValue = propertyValue;
    if (propertyValidationMap[dbProperty.type]) {
      const _valRes = propertyValidationMap[dbProperty.type].validationFunction(
        propertyValue
      );
      if (!_valRes.truthy) {
        validationDetails.push({
          d: eqSpaceMappingMsgs.PROPERTY_INVALID_DATA_TYPE,
          args: { type: dbProperty.type }
        });
      } else {
        convertedValue = propertyValidationMap[dbProperty.type].formattedValue(
          propertyValue
        ).convertedVal;
      }
    }
    return { validationDetails, convertedValue };
  };

  // Applicable on both element level and spatial element level
  const validateProperties = (
    properties: IPropertyData[],
    dbPropertyList: IDBPropertyObj[]
  ): {
    propertiesValidations: IPropertyValidation[];
    allValid: boolean;
    numErrors: number;
  } => {
    const _dbPropertyMap: { [propertyRoleName: string]: IDBPropertyObj } = {};
    _.forEach(dbPropertyList, (_dbP) => {
      _dbPropertyMap[_dbP.propertyRoleName] = _dbP;
    });
    let allValid = true;
    let numErrors = 0;
    const propertiesValidations = _.map(properties, (p) => {
      const _dbProperty = _dbPropertyMap[p.propertyKey] || null;
      let id = '';
      let type = '';
      let propertyValue = p.propertyValue;
      let isValid = true;
      const validationDetails = [];
      if (_dbProperty) {
        const { validationDetails: _vds, convertedValue } = validateProperty(
          p.propertyValue,
          _dbProperty
        );
        validationDetails.push(..._vds);
        id = _dbProperty.id;
        type = _dbProperty.type;
        propertyValue = convertedValue;
      } else {
        validationDetails.push({
          d: eqSpaceMappingMsgs.PROPERTY_DNE,
          args: { name: p.propertyKey }
        });
      }
      if (validationDetails.length > 0) {
        isValid = false;
        allValid = false;
      }
      return { ...p, propertyValue, id, isValid, validationDetails };
    });
    propertiesValidations.forEach((p) => {
      numErrors += p.validationDetails.filter(
        (_v) => _v.type !== 'child' && _v.type !== 'info'
      ).length;
    });
    return { propertiesValidations, allValid, numErrors };
  };

  // Individual Spatial Element level Validation
  // Spatial Element Name
  //    If new or exists
  //    If contains special characters
  //    name length < 255
  //    if empty association
  // disabled property validation
  const validateSpatialElement = (
    spatialElement: IAssociationData,
    allSpatialElements: IDBSpatialElement[],
    prevSpElements: { [key: string]: boolean }
  ): ISpatialEleValidation => {
    const spEleVal: ISpatialEleValidation = {
      ...spatialElement,
      isValid: true,
      // validatedProperties: [],
      validationDetails: []
    };

    if (spatialElement.associations.length === 0) {
      spEleVal.isValid = false;
      spEleVal.validationDetails.push({
        d: eqSpaceMappingMsgs.NO_ASSOC_REMOVE
      });
    } else {
      const d = !_.isEmpty(spEleVal.SpatialElementId)
        ? _.find(
            allSpatialElements,
            ({ id }) => id === spEleVal.SpatialElementId
          )
        : _.find(
            allSpatialElements,
            ({ name }) => name === spEleVal.SpatialElementName
          );

      if (d) {
        if (_.isEmpty(spEleVal.SpatialElementId)) {
          spEleVal.SpatialElementId = d.id;
        } else {
          if (spEleVal.SpatialElementName !== d.name) {
            spEleVal.isValid = false;
            spEleVal.validationDetails.push({
              d: eqSpaceMappingMsgs.SPATIAL_ELEMENT_NAME_MISMATCH,
              args: { id: spEleVal.SpatialElementId, dbSpEleName: d.name }
            });
          }
        }
      } else {
        if (!_.isEmpty(spEleVal.SpatialElementId)) {
          spEleVal.isValid = false;
          spEleVal.validationDetails.push({
            d: eqSpaceMappingMsgs.SPATIAL_ELEMENT_ID_DNE,
            args: { id: spEleVal.SpatialElementId }
          });
        }
        //  Check for Special Characters in new name
        if (!commonNameRegex.test(spEleVal.SpatialElementName)) {
          spEleVal.isValid = false;
          spEleVal.validationDetails.push({
            d: eqSpaceMappingMsgs.NAME_INVALID_SPECIAL_CHAR
          });
        }
        if (spEleVal.SpatialElementName.length > maxNameLength) {
          spEleVal.isValid = false;
          spEleVal.validationDetails.push({
            d: eqSpaceMappingMsgs.NAME_INVALID_MAX_LEN,
            args: { len: maxNameLength }
          });
        }
        if (spEleVal.isValid) {
          spEleVal.validationDetails.push({
            d: eqSpaceMappingMsgs.SPATIAL_ELE_NEW,
            type: 'info'
          });
        }
      }

      if (prevSpElements[spEleVal.SpatialElementName] === true) {
        spEleVal.isValid = false;
        spEleVal.validationDetails.push({
          d: eqSpaceMappingMsgs.DUPLICATE_ENTRIES
        });
      }

      // const {
      //   allValid: allPropertiesValid,
      //   propertiesValidations
      // } = validateProperties(spatialElement.properties, spElePropertyValFns);

      // if (!allPropertiesValid) {
      //   spEleVal.isValid = false;
      //   spEleVal.validationDetails.push({
      //     d: eqSpaceMappingMsgs.PROPERTIES_INVALID
      //   });
      // }
      // spEleVal.validatedProperties = propertiesValidations;
    }
    return spEleVal;
  };

  // All Spatial Elements Validation
  const validateAllSpatialElements = (
    associationData: IAssociationData[],
    allSpatialElements: IDBSpatialElement[]
  ): {
    numErrors: number;
    spatialElementsValidations: ISpatialEleValidation[];
  } => {
    const prevSpatialElements: { [spEleName: string]: boolean } = {};
    let numErrors = 0;
    const spatialElementsValidations: ISpatialEleValidation[] = _.map(
      associationData,
      (spatialEle) => {
        const _spEle = validateSpatialElement(
          spatialEle,
          allSpatialElements,
          prevSpatialElements
        );
        numErrors += _spEle.validationDetails.filter(
          (d) => d.type !== 'info' && d.type !== 'child'
        ).length;
        prevSpatialElements[_spEle.SpatialElementName] = true;
        return _spEle;
      }
    );

    return { numErrors, spatialElementsValidations };
  };

  // Individual Element Level Validation
  const validateElement = (
    element: IElementData,
    allElements: IDBElement[],
    prevElements: { [key: string]: boolean }
  ): { elementVal: IElementDataValidations; numPropErrors: number } => {
    const elementVal: IElementDataValidations = {
      ...element,
      validatedProperties: [],
      isValid: true,
      validationDetails: []
    };

    // Validate Name
    //    Check if does not exist
    // const d = !_.isEmpty(elementVal.ElementId)
    //   ? _.find(allElements, (ele) => ele.id === elementVal.ElementId)
    //   : _.find(
    //       allElements,
    //       (ele) =>
    //         ele.name === elementVal.ElementName &&
    //         _.includes(
    //           _.map(ele.types, lowerCaseNoSpaces),
    //           lowerCaseNoSpaces(elementVal.ElementType)
    //         )
    //     );

    const searchInElementList = (): {
      searchedEle: IDBElement | undefined;
      found: boolean;
    } => {
      let isfound = false;
      let searchedEle;
      if (!_.isEmpty(elementVal.ElementId)) {
        searchedEle = _.find(allElements, (e) => e.id === elementVal.ElementId);
        if (searchedEle) {
          isfound = true;
        }
      } else {
        const _allMatching = allElements.filter(
          (a) =>
            a.name === elementVal.ElementName &&
            _.includes(
              _.map(a.types, lowerCaseNoSpaces),
              lowerCaseNoSpaces(elementVal.ElementType)
            )
        );

        if (_allMatching.length > 1) {
          isfound = true;
          elementVal.isValid = false;
          elementVal.validationDetails.push({
            d: eqSpaceMappingMsgs.MULTIPLE_EQUIPMENTS_SAME_NAME
          });
        } else if (_allMatching.length === 1) {
          isfound = true;
          searchedEle = _allMatching[0];
        } else {
          isfound = false;
        }
      }
      return { searchedEle, found: isfound };
    };
    const { searchedEle: d, found } = searchInElementList();

    if (d) {
      if (_.isEmpty(elementVal.ElementId)) {
        elementVal.ElementId = d.id;
      } else {
        if (elementVal.ElementName !== d.name) {
          elementVal.isValid = false;
          elementVal.validationDetails.push({
            d: eqSpaceMappingMsgs.ELEMENT_NAME_MISMATCH,
            args: { id: elementVal.ElementId, dbEleName: d.name }
          });
        }
      }
    } else {
      elementVal.isValid = false;
      if (!_.isEmpty(elementVal.ElementId)) {
        elementVal.validationDetails.push({
          d: eqSpaceMappingMsgs.ELEMENT_ID_DNE,
          args: { id: elementVal.ElementId }
        });
      } else {
        if (!found) {
          elementVal.validationDetails.push({
            d: eqSpaceMappingMsgs.ELEMENT_TYPE_NAME_DNE,
            args: { name: elementVal.ElementName, type: elementVal.ElementType }
          });
        }
      }
    }

    if (prevElements[`${elementVal.ElementName}_${elementVal.ElementId}`]) {
      elementVal.isValid = false;
      elementVal.validationDetails.push({
        d: eqSpaceMappingMsgs.DUPLICATE_ENTRIES
      });
    }

    let numPropErrors = 0;
    // validate properties
    const {
      propertiesValidations,
      allValid,
      numErrors: _numPropErrors
    } = validateProperties(element.properties, d ? d.properties : []);
    if (!allValid) {
      elementVal.isValid = false;
      elementVal.validationDetails.push({
        d: eqSpaceMappingMsgs.PROPERTIES_INVALID
        // type: 'child'
      });
    }
    elementVal.validatedProperties = propertiesValidations;
    numPropErrors += _numPropErrors;

    return { elementVal, numPropErrors };
  };

  // Validate associations of individual spatial element
  const validateAssociation = (
    validatedSpatialElement: ISpatialEleValidation,
    allElements: IDBElement[]
  ): { numErrors: number; assocValidation: IAssociationDataValidations } => {
    let numErrors = 0;
    const assocValidation: IAssociationDataValidations = {
      ...validatedSpatialElement,
      allValid: true,
      validatedAssociations: []
    };

    const prevElements: { [eleNameUnderscoreEleId: string]: boolean } = {};
    let allValid = true;
    assocValidation.validatedAssociations = _.map(
      validatedSpatialElement.associations,
      (ele) => {
        const { elementVal: validatedEle, numPropErrors } = validateElement(
          ele,
          allElements,
          prevElements
        );
        numErrors += numPropErrors;
        numErrors += validatedEle.validationDetails.filter(
          (v) => v.type !== 'child' && v.type !== 'info'
        ).length;
        prevElements[
          `${validatedEle.ElementName}_${validatedEle.ElementId}`
        ] = true;
        allValid = allValid && validatedEle.isValid;
        return validatedEle;
      }
    );
    assocValidation.allValid = allValid && validatedSpatialElement.isValid;

    return { assocValidation, numErrors };
  };

  return { validateAssocData };
};
